import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MetaService } from '@core/services/meta.service';
import { UmbracoImage } from '@models/umbraco.model';
import { ImageScalePipe } from '@shared/image/image-scale/image-scale.pipe';
import { CropModes, ImageFormats } from '@shared/image/image.config';
import { isDefined } from 'src/app/@utils/is-defined';
import { IntersectionLazyImageDirective } from '../../intersection/intersection-lazy-image/intersection-lazy-image.directive';
import { NgClass, NgIf, NgStyle } from '@angular/common';

type Sizes = [smallWidth: number, mediumWidth: number, largeWidth: number];

@Component({
    selector: 'app-image',
    templateUrl: './image.component.html',
    styleUrls: ['./image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, IntersectionLazyImageDirective, NgStyle, NgClass]
})
export class ImageComponent implements OnInit {
    public src: string;

    public srcSet: string;

    @Input()
    public readonly image: UmbracoImage;

    @Input()
    public readonly height: number;

    @Input()
    public readonly width: number;

    @Input()
    public readonly index?: number;

    @Input()
    public readonly indexLoadingThreshold?: number;

    @Input()
    public readonly quality: number = 80;

    @Input()
    public readonly crop?: CropModes;

    @Input()
    public format?: ImageFormats;

    @Input()
    public readonly intersectionOptions?: IntersectionObserverInit;

    @Input()
    public style?: Partial<CSSStyleDeclaration>;

    constructor(
        private imageScalePipe: ImageScalePipe,
        private metaService: MetaService
    ) {}

    ngOnInit(): void {
        this.src = this.imageScalePipe.transform(
            this.image,
            this.width,
            this.height,
            this.crop,
            this.format,
            this.quality
        );

        // TODO For now we only use the large sizes
        // Though we should priotize to use smaller image sizes with srcset
        const [, , largeWidth] = this.getSizes(this.width);
        const [, , largeHeight] = this.getSizes(this.height);

        if (this.width && this.height) {
            this.srcSet = `
                ${this.imageScalePipe.transform(this.image, largeWidth, largeHeight, this.crop, this.format, this.quality)} 2x
            `;
        }

        if (isDefined(this.index) && this.index < 1) {
            this.metaService.setPreloadImage(this.src, this.srcSet, 'high');
        }
    }

    private getSizes(value: number): Sizes {
        return [value / 2, value, value * 2].map(val => Math.round(val)) as Sizes;
    }
}
